import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useClients } from '../../hooks/useClients';
import type { MeetingContact, MeetingContactInput } from '../../lib/meetingLogApi';
import { Select } from '../ui/Select';

// Create/edit modal for an external contact in the meetings directory.

interface Props {
  initial?: MeetingContact;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (input: MeetingContactInput) => void | Promise<void>;
}

const inputCls =
  'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
const labelCls = 'mb-1 block text-xs font-medium text-gray-600';

export function ContactFormModal({ initial, submitting, onClose, onSubmit }: Props) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    firstName: initial?.firstName ?? '',
    lastName: initial?.lastName ?? '',
    email: initial?.email ?? '',
    phone: initial?.phone ?? '',
    companyName: initial?.companyName ?? '',
    role: initial?.role ?? '',
    notes: initial?.notes ?? '',
    clientId: initial?.clientId ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const { data: clientsData } = useClients({ sort: 'name', limit: 100, active: 'true' });
  const clients = clientsData?.data ?? [];

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.firstName.trim()) {
      setError(t('meetings.attendees.firstNameRequired'));
      return;
    }
    try {
      await onSubmit({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        companyName: form.companyName.trim() || null,
        role: form.role.trim() || null,
        notes: form.notes.trim() || null,
        clientId: form.clientId || null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    }
  }

  const fields: { key: keyof typeof form; label: string; type?: string }[] = [
    { key: 'firstName', label: t('meetings.attendees.firstName') },
    { key: 'lastName', label: t('meetings.attendees.lastName') },
    { key: 'email', label: t('meetings.attendees.email'), type: 'email' },
    { key: 'phone', label: t('meetings.attendees.phone') },
    { key: 'companyName', label: t('meetings.attendees.company') },
    { key: 'role', label: t('meetings.attendees.role') },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {initial ? t('meetings.contacts.editTitle') : t('meetings.contacts.createTitle')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label={t('common.close')}
            >
              ✕
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.key}>
                <label className={labelCls}>{f.label}</label>
                <input
                  type={f.type ?? 'text'}
                  className={inputCls}
                  value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Select
                label={t('meetings.attendees.client')}
                searchable
                clearable
                value={form.clientId}
                onChange={(v) => set('clientId', v)}
                placeholder={t('meetings.attendees.noClient')}
                options={clients.map((c) => ({ value: c.id, label: c.name }))}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>{t('meetings.contacts.notes')}</label>
              <textarea
                className={inputCls}
                rows={3}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-red-600 sm:col-span-2">{error}</p>}
          </div>
          <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
